import {
  applyEvent, asTablePhase, emptyModel, fromSnapshot, isOnTurn,
  type Snapshot, type TableModel, type Turn, type WireEvent,
  type ScheduledSpeech, type SpeechCue,
} from "@guandan/protocol";
import type { Seat } from "@guandan/engine";

/**
 * The client half of the timed event stream.
 *
 * The server resolves a trick in well under a millisecond — three AI passes are
 * instantaneous — while a person needs seconds to watch it. Each event arrives
 * stamped with an `offset` (wait this long before showing it) and a `hold`
 * (dwell this long afterwards), so the client's job is pacing, not invention.
 *
 * Three things are kept apart:
 *
 *   authoritative  updated the moment an event arrives
 *   queue          received, not yet shown
 *   presented      what the player is looking at
 *
 * Input follows `presented`, never `authoritative`. A player may act only once
 * the queue has drained, which costs nothing — the server cannot advance
 * without them — and removes any chance of acting on a state they were never
 * shown.
 */

export type Cue =
  | { kind: "reset" }
  | { kind: "clearPresentation" }
  | { kind: "presentation"; presentation: import("@guandan/protocol").Presentation }
  /**
   * `power` is the combo's position on the bomb ladder, 0 for a normal play.
   * It has to travel with the cue: the sound and particle tiers key off it, and
   * a boolean alone made four kings land exactly like a four-card bomb.
   */
  | { kind: "play"; bomb: boolean; power: number; comboType: string; length: number; seat: Seat; seq: number }
  | { kind: "pass"; seat: Seat; seq: number }
  | { kind: "deal" }
  | { kind: "trick"; winner: Seat }
  | { kind: "out"; seat: Seat; place: number }
  | { kind: "handEnd" }
  | { kind: "matchEnd" }
  | { kind: "speech"; cue: SpeechCue; startAt?: number; endAt?: number };

export interface SchedulerSnapshot {
  readonly model: TableModel;
  readonly lag: number;
  readonly canAct: boolean;
}

export interface SchedulerHooks {
  /** Animation and audio cues, emitted as each event is presented. */
  readonly onCue?: (cue: Cue) => void;
  readonly onPresentationEnd?: () => void;
  /** Called as soon as authoritative events arrive, before their presentation time. */
  readonly onSpeechPrepare?: (cue: SpeechCue) => void;
  /** Authoritative server time estimate, not raw local wall time. */
  readonly now?: () => number;
  readonly setTimer?: (fn: () => void, ms: number) => number;
  readonly clearTimer?: (id: number) => void;
}

export interface ResumeRange {
  readonly from: number;
  readonly to: number;
}

interface ResumeTarget {
  readonly turn: Turn;
  readonly seat: Seat | null;
  readonly to: number;
}

export class EventScheduler {
  #authoritative: TableModel;
  #presented: TableModel;
  #queue: WireEvent[] = [];
  #timer: number | null = null;
  #listeners = new Set<() => void>();
  #hooks: Required<Pick<SchedulerHooks, "now" | "setTimer" | "clearTimer">> & SchedulerHooks;
  #readyTimer: number | null = null;
  #speechTimers = new Map<number, { key: string; cue: SpeechCue }>();
  #scheduledSpeech = new Set<string>();
  #snapshot: SchedulerSnapshot;
  #lastVoiceSeq = 0;
  #suspended = false;
  /**
   * A resume greeting and its replay arrive as two ordered WebSocket messages.
   * Keep the final turn aside until the replay has actually reached `to`, or
   * the UI can show "your turn" while the replay queue still blocks input.
   */
  #resumeTarget: ResumeTarget | null = null;

  constructor(seat: Seat | null, hooks: SchedulerHooks = {}) {
    this.#authoritative = emptyModel(seat);
    this.#presented = this.#authoritative;
    this.#snapshot = { model: this.#presented, lag: 0, canAct: false };
    this.#hooks = {
      now: hooks.now ?? (() => Date.now()),
      setTimer: hooks.setTimer ?? ((fn, ms) => globalThis.setTimeout(fn, ms) as unknown as number),
      clearTimer: hooks.clearTimer ?? ((id) => globalThis.clearTimeout(id)),
      ...hooks,
    };
  }

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  /**
   * What components render.
   *
   * A single object so `useSyncExternalStore` sees a new identity whenever any
   * of it changes. Reading `lag` or `canAct` off the instance during render
   * misses updates: taking delivery of events moves the authoritative model
   * without touching `presented`, so React compared the same object and never
   * re-rendered — the "catching up" state showed late.
   */
  getSnapshot = (): SchedulerSnapshot => this.#snapshot;

  /** What the player is looking at. */
  getPresented = (): TableModel => this.#presented;
  /** What the server has told us. Used for staleness, never for rendering. */
  getAuthoritative = (): TableModel => this.#authoritative;

  /** How many events are waiting to be shown. */
  get lag(): number {
    return this.#authoritative.seq - this.#presented.seq;
  }

  /** Diagnostics only. */
  get debug(): { queue: number; timerPending: boolean; resumePending: boolean } {
    return {
      queue: this.#queue.length,
      timerPending: this.#timer !== null,
      resumePending: this.#resumeTarget !== null,
    };
  }

  /**
   * May the player act?
   *
   * Only with the queue drained. Acting while events are still being presented
   * would mean acting on a table the player has not seen yet.
   */
  get canAct(): boolean {
    return !this.#suspended && this.#resumeTarget === null
      && this.#queue.length === 0
      && isOnTurn(this.#presented)
      && this.#hooks.now() >= this.#presented.turnReadyAt;
  }

  /** A cold start. Both views adopt the snapshot and the queue is dropped. */
  adopt(snapshot: Snapshot, seat: Seat | null): void {
    this.#cancel();
    this.#queue = [];
    this.#resumeTarget = null;
    this.#authoritative = fromSnapshot(snapshot, seat);
    this.#presented = this.#authoritative;
    this.#notify();
    this.#scheduleReadyNotification();
  }

  /**
   * A resume. When replayed events are pending, the presented view stays on the
   * last known turn until the queue reaches `range.to`. A zero-gap resume can
   * safely apply the server turn immediately.
   */
  resume(turn: Turn, seat: Seat | null, range: ResumeRange = { from: 0, to: 0 }): void {
    const patch = (model: TableModel): TableModel => ({
      ...model,
      seat,
      turn: turn.seat,
      open: turn.open,
      phase: asTablePhase(turn.phase),
      deadlineAt: turn.deadlineAt,
      turnReadyAt: turn.turnReadyAt,
      turnToken: turn.turnToken,
    });
    if (range.to > range.from) {
      // The greeting is authoritative immediately, so recovery can still use
      // its turn token if replay delivery stalls. Only the presented model is
      // held back until every missing event has been shown or folded.
      this.#authoritative = patch(this.#authoritative);
      this.#presented = { ...this.#presented, seat };
      this.#resumeTarget = { turn, seat, to: range.to };
      this.#notify();
      this.wake();
      return;
    }
    this.#resumeTarget = null;
    this.#authoritative = patch(this.#authoritative);
    // Only move what the player sees if there is nothing still to show them.
    if (this.#queue.length === 0) this.#presented = patch(this.#presented);
    this.#notify();
    this.#scheduleReadyNotification();
  }

  /**
   * Re-evaluate timers against authoritative time.
   *
   * Browsers may suspend timers in a background tab. Cancelling the stale
   * local timer and pumping again lets already-expired events fold immediately
   * when the page or socket wakes up.
   */
  wake(): void {
    if (this.#timer !== null) {
      this.#hooks.clearTimer(this.#timer);
      this.#timer = null;
    }
    this.#pump();
    this.#scheduleReadyNotification();
    this.#notify();
  }

  /** Take delivery of events: authoritative immediately, presented on a timer. */
  ingest(events: readonly WireEvent[]): void {
    for (const event of events) {
      if (event.seq <= this.#authoritative.seq) continue; // duplicate from a replay
      const actor = event.body.type === "played" || event.body.type === "passed"
        || event.body.type === "tributePaid" || event.body.type === "tributeReturned"
        ? (event.body.type === "tributePaid" || event.body.type === "tributeReturned" ? event.body.from : event.body.seat)
        : null;
      if (actor !== null) this.#cancelPendingSpeech(actor);
      this.#authoritative = applyEvent(this.#authoritative, event.body, event.seq);
      for (const cue of event.speech ?? []) this.#hooks.onSpeechPrepare?.(cue);
      if (event.body.type === "turn" && event.body.pendingSpeech !== undefined) {
        this.prepareSpeech(event.body.pendingSpeech);
      }
      this.#queue.push(event);
    }
    this.#reconcileAuthoritativeResume();
    this.#notify();
    this.#pump();
  }

  /** Live hesitation cue: independent of game seq and never replayed. */
  ingestSpeech(voiceSeq: number, startAt: number, endAt: number, cue: SpeechCue): void {
    if (voiceSeq <= this.#lastVoiceSeq) return;
    this.#lastVoiceSeq = voiceSeq;
    this.prepareSpeech({ startAt, endAt, cue });
  }

  /** Schedule a cue received early with a turn event or greeting. */
  prepareSpeech(speech: ScheduledSpeech): void {
    if (this.#suspended) return;
    const { startAt, endAt, cue } = speech;
    const key = `${cue.cueId}:${startAt}`;
    if (this.#scheduledSpeech.has(key)) return;
    this.#scheduledSpeech.add(key);
    this.#hooks.onSpeechPrepare?.(cue);
    const play = () => {
      if (this.#hooks.now() <= endAt) this.#hooks.onCue?.({ kind: "speech", cue, startAt, endAt });
    };
    const delay = Math.max(0, startAt - this.#hooks.now());
    let id = 0;
    id = this.#hooks.setTimer(() => { this.#speechTimers.delete(id); play(); }, delay);
    this.#speechTimers.set(id, { key, cue });
  }

  /**
   * Drop pending work without tearing the object down.
   *
   * Called when the socket goes away. Subscribers are deliberately kept: React
   * runs an effect's cleanup and setup back to back in development, and a
   * scheduler that cleared its listeners there would be revived by the second
   * mount with nothing left listening — the table would freeze while events
   * kept arriving.
   */
  setSuspended(suspended: boolean): void {
    this.#suspended = suspended;
    if (suspended) this.detach();
    this.#notify();
  }

  detach(): void {
    this.#cancel();
    this.#queue = [];
    this.#resumeTarget = null;
    for (const timer of this.#speechTimers.keys()) this.#hooks.clearTimer(timer);
    this.#speechTimers.clear();
    this.#scheduledSpeech.clear();
  }

  #cancelPendingSpeech(seat: Seat): void {
    for (const [timer, scheduled] of this.#speechTimers) {
      if (scheduled.cue.timing !== "live" || scheduled.cue.seat !== seat) continue;
      this.#hooks.clearTimer(timer);
      this.#speechTimers.delete(timer);
    }
  }

  /** Replay-only seek: rebuild a projection without timers or audiovisual cues. */
  seek(events: readonly WireEvent[]): void {
    const seat = this.#presented.seat;
    this.#cancel();
    this.#queue = [];
    this.#resumeTarget = null;
    this.#authoritative = emptyModel(seat);
    this.#presented = this.#authoritative;
    for (const event of events) {
      this.#authoritative = applyEvent(this.#authoritative, event.body, event.seq);
      this.#presented = this.#authoritative;
    }
    this.#notify();
  }

  #cancel(): void {
    this.#hooks.onCue?.({ kind: "reset" });
    for (const timer of this.#speechTimers.keys()) this.#hooks.clearTimer(timer);
    this.#speechTimers.clear();
    this.#scheduledSpeech.clear();
    if (this.#timer !== null) {
      this.#hooks.clearTimer(this.#timer);
      this.#timer = null;
    }
    if (this.#readyTimer !== null) {
      this.#hooks.clearTimer(this.#readyTimer);
      this.#readyTimer = null;
    }
  }

  #pump(): void {
    if (this.#suspended) return;
    if (this.#timer !== null) return;
    if (this.#queue.length === 0) {
      if (this.#finishResume()) this.#notify();
      return;
    }

    // Explicit catch-up: events whose authoritative dwell has already ended
    // are folded silently. Arrival time never creates a new local playback
    // timeline, and reconnect history does not crawl through stale animation.
    if ((this.#queue[0]?.readyAt ?? Infinity) <= this.#hooks.now()) {
      this.#hooks.onCue?.({ kind: "reset" });
      while ((this.#queue[0]?.readyAt ?? Infinity) <= this.#hooks.now()) {
        const event = this.#queue.shift() as WireEvent;
        this.#presented = applyEvent(this.#presented, event.body, event.seq);
      }
      this.#finishResume();
      this.#notify();
      // Folding can land the queue exactly on this seat's turn while its
      // authoritative start time is still ahead. Without this the table would
      // sit un-actionable with nothing left to wake it: no further event is
      // coming, because the server is waiting on this player.
      this.#scheduleReadyNotification();
    }

    const next = this.#queue[0];
    if (next === undefined) return;

    const delay = Math.max(0, next.presentAt - this.#hooks.now());
    this.#timer = this.#hooks.setTimer(() => {
      this.#timer = null;
      this.#queue.shift();
      this.#presented = applyEvent(this.#presented, next.body, next.seq);
      this.#finishResume();
      this.#emit(next);
      this.#notify();
      this.#scheduleReadyNotification();
      this.#pump();
    }, delay);
  }

  #patchTurn(model: TableModel, target: ResumeTarget): TableModel {
    const { turn, seat } = target;
    return {
      ...model,
      seat,
      turn: turn.seat,
      open: turn.open,
      phase: asTablePhase(turn.phase),
      deadlineAt: turn.deadlineAt,
      turnReadyAt: turn.turnReadyAt,
      turnToken: turn.turnToken,
    };
  }

  #reconcileAuthoritativeResume(): void {
    const target = this.#resumeTarget;
    if (target === null || this.#authoritative.seq < target.to) return;
    this.#authoritative = this.#patchTurn(this.#authoritative, target);
  }

  #finishResume(): boolean {
    const target = this.#resumeTarget;
    if (target === null || this.#queue.length > 0 || this.#presented.seq < target.to) return false;
    this.#authoritative = this.#patchTurn(this.#authoritative, target);
    this.#presented = this.#patchTurn(this.#presented, target);
    this.#resumeTarget = null;
    return true;
  }

  #scheduleReadyNotification(): void {
    if (this.#readyTimer !== null) this.#hooks.clearTimer(this.#readyTimer);
    this.#readyTimer = null;
    if (this.#suspended || !isOnTurn(this.#presented) || this.canAct) return;
    const delay = this.#presented.turnReadyAt - this.#hooks.now();
    if (delay <= 0) return;
    this.#readyTimer = this.#hooks.setTimer(() => {
      this.#readyTimer = null;
      this.#notify();
    }, delay);
  }

  #emit(event: WireEvent): void {
    this.#hooks.onPresentationEnd?.();
    const onCue = this.#hooks.onCue;
    if (event.body.type === "handStarted") onCue?.({ kind: "reset" });
    if (onCue !== undefined) for (const cue of event.speech ?? []) onCue({ kind: "speech", cue });
    if (onCue === undefined) return;
    if (event.presentation !== undefined) onCue({ kind: "presentation", presentation: { ...event.presentation, durationMs: Math.max(0, Math.min(event.presentation.durationMs, event.readyAt - this.#hooks.now())) } });
    const body = event.body;
    switch (body.type) {
      case "played":
        onCue({ kind: "play", bomb: body.combo.power > 0, power: body.combo.power, comboType: body.combo.type, length: body.combo.length, seat: body.seat, seq: event.seq });
        return;
      case "passed": onCue({ kind: "pass", seat: body.seat, seq: event.seq }); return;
      case "dealt": onCue({ kind: "deal" }); return;
      case "trickEnded": onCue({ kind: "trick", winner: body.winner }); return;
      case "playerOut": onCue({ kind: "out", seat: body.seat, place: body.place }); return;
      case "handEnded": onCue({ kind: "handEnd" }); return;
      case "matchEnded": onCue({ kind: "matchEnd" }); return;
      default: return;
    }
  }

  #notify(): void {
    this.#snapshot = { model: this.#presented, lag: this.lag, canAct: this.canAct };
    for (const listener of [...this.#listeners]) listener();
  }
}
